<template>
  <div class="w-full base-skeleton">
    <div
      v-for="(width, index) in lineWidths"
      :key="index"
      class="bg-grey-200 rounded animate-shine base-skeleton__line"
      :class="{ 'base-skeleton__line--visible': !progressive || index < visibleCount }"
      :style="{
        width,
        height: `${height}px`,
        marginBottom: index === lineWidths.length - 1 ? '0' : `${spacing}px`,
      }"
    />
  </div>
</template>

<script setup>
import {
  computed, onBeforeUnmount, onMounted, ref,
} from 'vue';

const props = defineProps({
  lines: {
    type: Number,
    default: 1,
  },
  minWidthPercent: {
    type: Number,
    default: 30,
  },
  maxWidthPercent: {
    type: Number,
    default: 100,
  },
  height: {
    type: Number,
    default: 16,
  },
  spacing: {
    type: Number,
    default: 8,
  },
  progressive: {
    type: Boolean,
    default: false,
  },
  intervalMs: {
    type: Number,
    default: 1500,
  },
});

const actualLines = computed(() => Math.max(1, props.lines));

const getRandomWidth = () => {
  const randomPercent = Math.floor(
    Math.random() * (props.maxWidthPercent - props.minWidthPercent + 1) + props.minWidthPercent,
  );
  return `${randomPercent}%`;
};

const lineWidths = computed(() => Array.from({ length: actualLines.value }, () => getRandomWidth()));

const visibleCount = ref(props.progressive ? 1 : actualLines.value);
let intervalId = null;

onMounted(() => {
  if (props.progressive) {
    intervalId = setInterval(() => {
      if (visibleCount.value >= actualLines.value) {
        clearInterval(intervalId);
        intervalId = null;
        return;
      }
      visibleCount.value += 1;
    }, props.intervalMs);
  }
});

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<style lang="scss">
@use "@sass/base/keyframes";
@use "@sass/design-system/variables" as ds-variables;

.base-skeleton {
  .animate-shine {
    animation: shine 1.5s linear infinite;
    background-image: linear-gradient(
      90deg,
      ds-variables.$grey-200 0%,
      ds-variables.$grey-300 50%,
      ds-variables.$grey-200 100%
    );
    background-size: 200% 100%;
  }

  &__line {
    opacity: 0;
    transform: translateY(-2px);
    transition: opacity 0.25s ease, transform 0.25s ease;

    &--visible {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
